import { Button, DialogOverlay, DialogOverlayActions, DialogOverlayFooter, Paragraph } from '@fs/zion-ui'
import React, { useContext } from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../../hooks/useNotifications'
import { updateGroupMember } from '../../../../services/groupsService'
import { UserContext } from '../../UserContext'
import DemotePromoteUserGroupsList from '../DemotePromoteUserGroupsList'

export default function DemotePromoteUserDialogOverlay({
  memberSummaries,
  refreshUserGroupsDataAndControls,
  ...overlay
}) {
  const { t } = useTranslation()
  const { error } = useNotifications()
  const { currentUser } = useContext(UserContext)
  const memberSummariesToDemote = getMembersToDemotePromote(memberSummaries, 'ADMIN')
  const memberSummariesToPromote = getMembersToDemotePromote(memberSummaries, 'MEMBER')
  const headingText = getHeadingText(memberSummaries, memberSummariesToDemote, memberSummariesToPromote)
  const bodyText = getBodyText(memberSummaries, memberSummariesToDemote, memberSummariesToPromote)

  const confirmBtnOnClick = async () => {
    overlay.close()
    try {
      await Promise.all(
        memberSummariesToDemote.map((memberSummary) => {
          const groupMemberUpdateProperties = {
            status: 'MEMBER',
            notifyOnJoin: memberSummary.notifyOnJoin,
          }
          return updateGroupMember(memberSummary.id, currentUser.dmcUser.cisId, groupMemberUpdateProperties)
        })
      )
    } catch (err) {
      error(
        memberSummariesToDemote.length > 1
          ? t('user.errors.demotingUser', {
              defaultValue: `Error demoting user in groups: ${err.message || ''}`,
              message: err.message || '',
            })
          : t('user.errors.demotingUser', {
              defaultValue: `Error demoting user in group: ${err.message || ''}`,
              message: err.message || '',
            }),
        err
      )
    }
    try {
      await Promise.all(
        memberSummariesToPromote.map((memberSummary) => {
          const groupMemberUpdateProperties = {
            status: 'ADMIN',
            notifyOnJoin: memberSummary.notifyOnJoin,
          }
          return updateGroupMember(memberSummary.id, currentUser.dmcUser.cisId, groupMemberUpdateProperties)
        })
      )
    } catch (err) {
      error(
        memberSummariesToPromote.length > 1
          ? t('user.errors.promotingUsers', {
              defaultValue: `Error promoting user in groups: ${err.message || ''}`,
              message: err.message || '',
            })
          : t('user.errors.promotingUser', {
              defaultValue: `Error promoting user in group: ${err.message || ''}`,
              message: err.message || '',
            }),
        err
      )
    }
    refreshUserGroupsDataAndControls()
  }

  return (
    <DialogOverlay id="Demote/Promote User overlay" {...overlay} title={t(headingText.key, headingText.value)}>
      <Paragraph size="sm">{t(bodyText.key, bodyText.value)}</Paragraph>
      <DemotePromoteUserGroupsList
        memberSummariesToDemote={memberSummariesToDemote}
        memberSummariesToPromote={memberSummariesToPromote}
      />
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" onClick={confirmBtnOnClick}>
            {t('action.confirm', 'Confirm')}
          </Button>
          <Button id="Demote/Promote User Cancel button" emphasis="medium" onClick={overlay.close}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

function getMembersToDemotePromote(memberSummaries, memberType) {
  return memberSummaries.filter((memberSummary) => memberSummary.memberStatus === memberType)
}

function getHeadingText(members, membersToDemote, membersToPromote) {
  const headingText = {
    key: 'user.groups.dialogOverlay.demotePromote.heading',
    value: 'Demote & Promote User',
  }
  if (members.length === membersToDemote.length) {
    headingText.key = 'user.groups.dialogOverlay.demote.heading'
    headingText.value = 'Demote User'
  } else if (members.length === membersToPromote.length) {
    headingText.key = 'user.groups.dialogOverlay.promote.heading'
    headingText.value = 'Promote User'
  }
  return headingText
}

function getBodyText(members, membersToDemote, membersToPromote) {
  const bodyText = {
    key: 'user.groups.dialogOverlay.demotePromote.body',
    value: 'Are you sure you want to demote and promote this user in these groups?',
  }
  if (members.length === membersToDemote.length) {
    if (members.length === 1) {
      bodyText.key = 'user.groups.dialogOverlay.demote.body.singleMember'
      bodyText.value = 'Are you sure you want to demote this user from Admin to Member in this group?'
    } else {
      bodyText.key = 'user.groups.dialogOverlay.demote.body.multipleMembers'
      bodyText.value = 'Are you sure you want to demote this user from Admin to Member in these groups?'
    }
  } else if (members.length === membersToPromote.length) {
    if (members.length === 1) {
      bodyText.key = 'user.groups.dialogOverlay.promote.body.singleMember'
      bodyText.value = 'Are you sure you want to promote this user from Member to Admin in this group?'
    } else {
      bodyText.key = 'user.groups.dialogOverlay.promote.body.multipleMembers'
      bodyText.value = 'Are you sure you want to promote this user from Member to Admin in these groups?'
    }
  }
  return bodyText
}
